import { useCallback, useEffect, useRef, useState } from "react";
import { useFlux } from "../direct/use-flux";
import { lireCle } from "./cle";
import { nomDeScene, obsDeLaPage } from "./obs";

/**
 * L'overlay `scene` (`/overlay/scene#cle=...`) : la page invisible qui dit a Castellan, pendant tout le
 * stream, quelle scene OBS affiche. La regie la lit dans « En direct ».
 *
 * - **Rien de visible, jamais** : elle rend `null`, en ligne comme hors ligne, cle refusee comme cle absente.
 *   Un pixel d'overlay passe dans le stream et les viewers le verraient ; la panne se lit dans la regie, qui
 *   liste les overlays connectes et met en rouge celui qui manque (l'ecart 2 du plan Q1).
 * - **Il ouvre le flux overlay sous le nom `scene`**, avec la cle en `Authorization: Cle <cle>` (`cle.ts`). C'est
 *   ce qui le fait apparaitre dans les overlays connectes ; le flux se reconnecte seul (`direct/flux.ts`).
 * - **Il poste la scene courante au chargement** (`getCurrentScene`) **et chaque `obsSceneChanged`**, par
 *   `POST /api/overlay/scene`. Les envois partent l'un apres l'autre et une rafale n'envoie que la derniere scene ; chacun a 10 s pour repondre.
 * - **Il la reposte apres chaque reconnexion du flux et apres chaque `reprise`** : la scene vit en memoire dans
 *   le serveur, qui a pu redemarrer et la perdre. Un envoi qui a echoue est refait a la reconnexion suivante.
 * - **Hors d'OBS** (`window.obsstudio` absent) ou sans cle, il n'ouvre rien : il ne s'inscrirait pas parmi les
 *   overlays connectes sans etre dans OBS.
 */

/** Le nom que l'overlay se donne : sur le flux (`?nom=`) et comme source du signalement. */
const NOM = "scene";

/** Un envoi qui n'a pas de reponse au bout de ce delai est abandonne. */
const DELAI_ENVOI_MS = 10_000;

export function SceneOverlay(): null {
  // La cle est lue une fois, au premier rendu : elle quitte alors l'adresse.
  const [cle] = useState(() => lireCle());
  const [obs] = useState(() => obsDeLaPage());
  const pret = cle !== null && obs !== undefined;

  /** Ce que la page sait : la scene courante, si le dernier envoi a echoue, combien de fois le flux s'est ouvert. */
  const etat = useRef({ courante: null as string | null, echec: false, connexions: 0 });
  /** L'envoi en cours, et la derniere scene demandee pendant ce temps : une rafale n'en envoie que la derniere. */
  const envoi = useRef({ enCours: false, attente: null as string | null });
  const actif = useRef(true);

  const poster = useCallback(
    (nom: string): void => {
      envoi.current.attente = nom;
      if (envoi.current.enCours) return;
      envoi.current.enCours = true;
      void (async () => {
        try {
          while (actif.current && envoi.current.attente !== null) {
            const aEnvoyer = envoi.current.attente;
            envoi.current.attente = null;
            try {
              const reponse = await fetch("/api/overlay/scene", {
                method: "POST",
                headers: { authorization: `Cle ${cle}`, "content-type": "application/json" },
                body: JSON.stringify({ nom: aEnvoyer, source: NOM }),
                // Un serveur qui ne repond pas ne doit pas bloquer les scenes suivantes.
                signal: AbortSignal.timeout(DELAI_ENVOI_MS),
              });
              etat.current.echec = !reponse.ok;
            } catch {
              // La page ne dit rien de sa panne : l'envoi sera refait a la reconnexion ou a la reprise.
              etat.current.echec = true;
            }
          }
        } finally {
          envoi.current.enCours = false;
        }
      })();
    },
    [cle],
  );

  function reposter(): void {
    const { courante } = etat.current;
    if (courante !== null) poster(courante);
  }

  useFlux(
    pret ? `/api/overlay/flux?nom=${NOM}` : null,
    cle === null ? undefined : { authorization: `Cle ${cle}` },
    {
      onEtat: (suivant) => {
        if (suivant.genre !== "connecte") return;
        etat.current.connexions += 1;
        // La premiere ouverture n'a rien a refaire, sauf un envoi qui a echoue ; les suivantes sont des reconnexions.
        if (etat.current.connexions > 1 || etat.current.echec) reposter();
      },
      onRelire: reposter,
    },
  );

  useEffect(() => {
    actif.current = true;
    if (!pret) return;

    // OBS repond a `getCurrentScene` quand il veut : une reponse arrivee apres un changement de scene serait
    // plus vieille que lui et ne doit pas l'ecraser.
    let changee = false;
    function signaler(nom: string): void {
      etat.current.courante = nom;
      poster(nom);
    }
    function surChangement(evenement: Event): void {
      const nom = nomDeScene(evenement);
      if (nom === null) return;
      changee = true;
      signaler(nom);
    }

    window.addEventListener("obsSceneChanged", surChangement);
    obs.getCurrentScene?.((scene) => {
      if (changee) return;
      if (typeof scene.name === "string" && scene.name !== "") signaler(scene.name);
    });
    return () => {
      actif.current = false;
      window.removeEventListener("obsSceneChanged", surChangement);
    };
  }, [pret, obs, poster]);

  return null;
}
